import z, { RefinementCtx, ZodIssueCode } from 'zod'
import { format, parseISO } from 'date-fns'
import { NonEmptyArray } from '@reward-platform/tsutil'
import { countries, Country, stateOrProvinceMap } from '@reward-platform/utils'
import { eighteenOrOlder, isDateOfBirthValidForTraveler } from './is-date-of-birth-valid'
import {
  createDateSchema,
  fieldCannotBeEmpty,
  fieldCannotBeMoreThanNChars,
  fieldMustBeAtLeastNChars,
  fieldMustBeSelected,
  fieldNotValid,
  getTranslationMessage,
  TranslationFunc,
  createNonEmptyStringSchema,
  notRequiredSchema,
  optionalField,
  createPhoneNumberSchema,
} from '../common'
import {
  FlightHotelTravelerContext,
  FlightHotelTraveler,
  ExperienceTraveler,
  ExperienceTravelerContext,
} from '../helpers/traveler-context'
import { isPassportExpirationDateValid } from './is-passport-expiration-date-valid'
import { isDateOfBirthWithinInterval } from './is-date-of-birth-within-interval'

const FIRST_NAME_MIN_LENGTH = 2
const LAST_NAME_MIN_LENGTH = 2
const MEASUREMENT_MAX_LENGTH = 50

export const TITLE_MR = 'Mr' as const
export const TITLE_MRS = 'Mrs' as const
const TITLE_MISS = 'Miss' as const
export const TITLE_MS = 'Ms' as const

export const TITLE_OPTIONS = [TITLE_MS, TITLE_MR, TITLE_MISS, TITLE_MRS] as const

export type Title = (typeof TITLE_OPTIONS)[number]

const createTitleSchema = (t?: TranslationFunc) =>
  z.enum(
    [TITLE_MS, TITLE_MR, TITLE_MISS, TITLE_MRS],
    getTranslationMessage({ key: fieldMustBeSelected, field: 'common.forms.labels.title', t })
  )

export const FEMALE = 'FEMALE' as const
export const MALE = 'MALE' as const
export const GENDER_OPTIONS = [FEMALE, MALE] as const

export type Gender = (typeof GENDER_OPTIONS)[number]

const getUTCDateString = (date: Date): string => {
  const year = date.getUTCFullYear().toString().padStart(4, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const day = date.getUTCDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getCalendarDate = (value: string): Date | string => {
  const calendarDate = /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0]
  return calendarDate ? parseISO(calendarDate) : value
}

const createGenderSchema = (t?: TranslationFunc) =>
  z.enum(
    [FEMALE, MALE],
    getTranslationMessage({ key: fieldMustBeSelected, field: 'common.forms.labels.gender', t })
  )

const mainTravelerEmailRegex =
  /^(?=.{1,64}@)[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*@(?=.{1,255}$)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]+)$/

export const createDateFormattedAsStringSchema = (field?: string, t?: TranslationFunc) =>
  z.preprocess(
    (arg) => {
      if (arg instanceof Date) {
        return parseISO(getUTCDateString(arg))
      }

      if (typeof arg === 'string') {
        return getCalendarDate(arg)
      }

      return arg
    },
    createDateSchema(field, t).transform((dob) => format(dob, 'yyyy-MM-dd'))
  )

const preprocessString = <T extends z.ZodType>(schema: T, field: string, t?: TranslationFunc) =>
  z.preprocess(
    (val) => (typeof val === 'string' && !val.trim().length ? undefined : val),
    schema.or(
      z
        .string()
        .trim()
        .min(1, getTranslationMessage({ key: fieldCannotBeEmpty, field, t }))
    )
  )

type DateOfBirthValidatorForFlightHotelTravelerParams = {
  traveler: FlightHotelTraveler
  dateOfBirth?: string
  ctx: RefinementCtx
  t?: TranslationFunc
}

const dateOfBirthValidatorForFlightHotelTraveler = ({
  traveler,
  dateOfBirth,
  ctx,
  t,
}: DateOfBirthValidatorForFlightHotelTravelerParams) => {
  if (traveler.requireDateOfBirth && dateOfBirth) {
    const expectedAge = traveler.age ?? eighteenOrOlder
    const { isValid, actual, expected } = isDateOfBirthValidForTraveler({
      traveler,
      dateOfBirth,
      expectedAge,
    })

    if (!isValid) {
      if (expectedAge === eighteenOrOlder) {
        ctx.addIssue({
          code: ZodIssueCode.too_small,
          inclusive: true,
          minimum: 18,
          origin: 'date',
          message:
            getTranslationMessage({ key: 'date_of_birth_adult_traveler_mismatch_error', t })
              ?.message ??
            `The traveller must be eighteen years or older at the point of travel: ${actual}`,
          path: ['dateOfBirth'],
        })
      } else {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message:
            getTranslationMessage({ key: 'date_of_birth_mismatch_error', t })?.message ??
            `Date of birth must match the traveller age at the point of travel, expected: ${expected}, actual: ${actual}`,
          path: ['dateOfBirth'],
        })
      }
    }
  }
}

type DateOfBirthValidatorForExperienceTravelerParams = {
  traveler: ExperienceTraveler
  dateOfBirth?: string
  ctx: RefinementCtx
  startDate: string
  t?: TranslationFunc
}

const dateOfBirthValidatorForExperienceTraveler = ({
  traveler,
  dateOfBirth,
  ctx,
  startDate,
  t,
}: DateOfBirthValidatorForExperienceTravelerParams) => {
  if (traveler.requireDateOfBirth && dateOfBirth) {
    const { startAge, endAge } = traveler

    const { isValid } = isDateOfBirthWithinInterval({
      startDate,
      dateOfBirth,
      startAge,
      endAge,
    })

    if (!isValid) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message:
          getTranslationMessage({
            key: 'date_of_birth_age_range_mismatch_error',
            values: { startAge, endAge },
            t,
          })?.message ?? `Date of birth must match provided age range (${startAge} - ${endAge})`,
      })
    }
  }
}

export const createFirstNameSchema = (t?: TranslationFunc) => {
  const field = 'common.forms.labels.first_name'
  const message = getTranslationMessage({
    key: fieldMustBeAtLeastNChars,
    field,
    t,
    values: {
      n: FIRST_NAME_MIN_LENGTH,
    },
  })
  return z.string(message).trim().min(FIRST_NAME_MIN_LENGTH, message)
}

export const createLastNameSchema = (t?: TranslationFunc) => {
  const field = 'common.forms.labels.last_name'
  const message = getTranslationMessage({
    key: fieldMustBeAtLeastNChars,
    field,
    t,
    values: {
      n: LAST_NAME_MIN_LENGTH,
    },
  })
  return z.string(message).trim().min(LAST_NAME_MIN_LENGTH, message)
}

const createBaseFlightHotelTravelerDetailsSchema = (
  traveler: FlightHotelTraveler,
  t?: TranslationFunc
) => {
  return z
    .object({
      id: z.literal(traveler.id),
      ageGroup: z.literal(traveler.ageGroup),
      age: optionalField(
        traveler.age != null
          ? z.preprocess((val) => Number(val), z.literal(traveler.age))
          : notRequiredSchema
      ),
      title: optionalField(
        traveler.requireTitle
          ? preprocessString(createTitleSchema(t), 'common.forms.labels.title', t)
          : notRequiredSchema
      ),
      firstName: optionalField(traveler.requireName ? createFirstNameSchema(t) : notRequiredSchema),
      familyName: optionalField(traveler.requireName ? createLastNameSchema(t) : notRequiredSchema),
      dateOfBirth: optionalField(
        traveler.requireDateOfBirth
          ? createDateFormattedAsStringSchema('common.forms.labels.date_of_birth', t)
          : notRequiredSchema
      ),
      gender: optionalField(
        traveler.requireGender
          ? preprocessString(createGenderSchema(t), 'common.forms.labels.gender', t)
          : notRequiredSchema
      ),
    })
    .superRefine(({ dateOfBirth }, ctx) => {
      dateOfBirthValidatorForFlightHotelTraveler({ traveler, dateOfBirth, ctx })
    })
}

const isValidStateOrProvince = (country: Country['value'], stateOrProvince: string | undefined) => {
  if (!country || !stateOrProvinceMap || !stateOrProvinceMap[country]) {
    return true
  }
  return stateOrProvinceMap[country].find(({ value }) => value === stateOrProvince) ?? false
}

const validUSZipCode = /^\d{5}(-\d{4})?$/i
const isValidCAPostalCode = /^[a-z]\d[a-z] \d[a-z]\d$/i
const isValidOtherPostalCode = /^[a-z0-9\- ]{4,9}$/i

const parsePostcode = (
  country: Country['value'],
  postcode: string
): { parsed: string | undefined; isValid: boolean } => {
  const trimmedPostcode = postcode.trim().replaceAll(/ +/g, ' ')
  switch (country) {
    case undefined:
      return { parsed: trimmedPostcode, isValid: true }
    case 'US':
      return { parsed: trimmedPostcode, isValid: validUSZipCode.test(trimmedPostcode) }
    case 'CA':
      return { parsed: trimmedPostcode, isValid: isValidCAPostalCode.test(trimmedPostcode) }
    default:
      return { parsed: trimmedPostcode, isValid: isValidOtherPostalCode.test(trimmedPostcode) }
  }
}

export const createAddressSchema = (t?: TranslationFunc) => {
  return z
    .object({
      addressLineOne: createNonEmptyStringSchema({
        field: 'common.forms.labels.address_line_one',
        t,
      }),
      addressLineTwo: z.string().trim().optional(),
      city: createNonEmptyStringSchema({
        field: 'common.forms.labels.town_city',
        t,
      }),
      stateOrProvince: z.string().trim().optional(),
      postcode: createNonEmptyStringSchema({
        field: 'common.forms.labels.postcode',
        t,
      }),
      country: z.enum(
        countries.map(({ value }) => value) as NonEmptyArray<Country['value']>,
        getTranslationMessage({ key: fieldNotValid, field: 'common.forms.labels.country', t })
      ),
    })
    .superRefine((val, ctx) => {
      const { country, postcode, stateOrProvince } = val
      if (!isValidStateOrProvince(country, stateOrProvince)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['stateOrProvince'],
          message: getTranslationMessage({
            key: fieldNotValid,
            field: 'common.forms.labels.state_or_province',
            t,
          })?.message,
        })
      }

      const { isValid: isValidPostcode } = parsePostcode(country, postcode)
      if (!isValidPostcode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['postcode'],
          message: getTranslationMessage({
            key: fieldNotValid,
            field: 'common.forms.labels.postcode',
            t,
          })?.message,
        })
      }
    })
}

export type TravelerDetailsAddress = z.infer<ReturnType<typeof createAddressSchema>>

const createAdditionalTravelersSchema = <T extends { id: string }, R extends z.ZodType>(
  additionalTravelers: T[],
  createSchemaForTraveler: (traveler: T, t?: TranslationFunc) => R,
  t?: TranslationFunc
) => {
  if (additionalTravelers.length === 0) {
    // optional: zod 4 rejects a missing key unless its schema is optional
    return z.undefined().optional()
  }

  const orderedIds = additionalTravelers.map(({ id }) => id)
  const [firstTravelerSchema, ...otherTravelerSchemas] = additionalTravelers.map((traveler) =>
    createSchemaForTraveler(traveler, t)
  )
  // zod 4 cannot infer the output of a tuple whose length is only known at runtime
  const tupleSchema = z.tuple([
    firstTravelerSchema,
    ...otherTravelerSchemas,
  ]) as unknown as z.ZodType<z.output<R>[]>

  return z.preprocess((val) => {
    const unorderedTravelers = val as Array<{ id: string }>
    return unorderedTravelers
      .slice()
      .sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id))
  }, tupleSchema)
}

const createMainTravelerAdditionalFieldsSchema = (t?: TranslationFunc) =>
  z.object({
    phone: createPhoneNumberSchema(t),
    emailAddress: createNonEmptyStringSchema({
      field: 'common.forms.labels.email_address',
      t,
    }).regex(
      mainTravelerEmailRegex,
      getTranslationMessage({
        key: fieldNotValid,
        field: 'common.forms.labels.email_address',
        t,
      })
    ),
    address: createAddressSchema(t),
    isAccountHolder: z.boolean().optional(),
  })

export const createAccountHolderEmailAddressSchema = (t?: TranslationFunc) => {
  return createNonEmptyStringSchema({
    field: 'pages.orders.traveller_details.account_holder_email',
    t,
  }).email(
    getTranslationMessage({
      key: fieldNotValid,
      field: 'pages.orders.traveller_details.account_holder_email',
      t,
    })
  )
}

export const createFlightHotelTravelerDetailsSchema = (
  context: FlightHotelTravelerContext,
  t?: TranslationFunc
) => {
  const { mainTraveler, additionalTravelers } = context

  return z.object({
    accountHolderEmailAddress: createAccountHolderEmailAddressSchema(t),
    mainTraveler: createBaseFlightHotelTravelerDetailsSchema(mainTraveler, t).and(
      createMainTravelerAdditionalFieldsSchema(t)
    ),
    additionalTravelers: createAdditionalTravelersSchema(
      additionalTravelers,
      createBaseFlightHotelTravelerDetailsSchema,
      t
    ),
  })
}

export type FlightHotelTravelerDetails = z.infer<
  ReturnType<typeof createFlightHotelTravelerDetailsSchema>
>

const createMeasurementSchema = (field: string, t?: TranslationFunc) =>
  z.object({
    unit: createNonEmptyStringSchema({
      field,
      t,
    }),
    value: createNonEmptyStringSchema({
      field,
      t,
    }).max(
      MEASUREMENT_MAX_LENGTH,
      getTranslationMessage({
        key: fieldCannotBeMoreThanNChars,
        field,
        t,
        values: {
          n: MEASUREMENT_MAX_LENGTH,
        },
      })
    ),
  })

type PassportExpiryValidatorParams = {
  startDate: string
  traveler: ExperienceTraveler
  passportExpiry?: string
  ctx: RefinementCtx
  t?: TranslationFunc
}

const passportExpiryValidator = ({
  startDate,
  ctx,
  traveler,
  passportExpiry,
  t,
}: PassportExpiryValidatorParams) => {
  if (!traveler.requirePassportExpiry || !passportExpiry) {
    return
  }

  if (!isPassportExpirationDateValid({ startDate, passportExpiry })) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message:
        getTranslationMessage({
          key: 'passport_expiration_date_not_valid',
          t,
        })?.message ?? 'Passport expiration date must be after the experience start date',
    })
  }
}

const createBaseExperienceTravelerDetailsSchema =
  (startDate: string) => (traveler: ExperienceTraveler, t?: TranslationFunc) => {
    return z.object({
      id: z.literal(traveler.id),
      ageGroup: z.literal(traveler.ageGroup),
      firstName: createFirstNameSchema(t),
      familyName: createLastNameSchema(t),
      dateOfBirth: optionalField(
        traveler.requireDateOfBirth
          ? createDateFormattedAsStringSchema('common.forms.labels.date_of_birth', t).superRefine(
              (val, ctx) => {
                dateOfBirthValidatorForExperienceTraveler({
                  startDate,
                  traveler,
                  dateOfBirth: val,
                  ctx,
                  t,
                })
              }
            )
          : notRequiredSchema
      ),
      height: optionalField(
        traveler.requireHeight
          ? createMeasurementSchema('common.forms.labels.height', t)
          : notRequiredSchema
      ),
      weight: optionalField(
        traveler.requireWeight
          ? createMeasurementSchema('common.forms.labels.weight', t)
          : notRequiredSchema
      ),
      passportNumber: optionalField(
        traveler.requirePassportNumber
          ? createNonEmptyStringSchema({ field: 'common.forms.labels.passport_number', t })
          : notRequiredSchema
      ),
      passportNationality: optionalField(
        traveler.requirePassportNationality
          ? createNonEmptyStringSchema({ field: 'common.forms.labels.passport_nationality', t })
          : notRequiredSchema
      ),
      passportExpiry: optionalField(
        traveler.requirePassportExpiry
          ? createDateFormattedAsStringSchema(
              'common.forms.labels.passport_expiration_date',
              t
            ).superRefine((val, ctx) => {
              passportExpiryValidator({ startDate, traveler, passportExpiry: val, ctx, t })
            })
          : notRequiredSchema
      ),
    })
  }

export const createExperienceTravelerDetailsSchema = (
  context: ExperienceTravelerContext,
  t?: TranslationFunc
) => {
  const { mainTraveler, additionalTravelers, startDate } = context
  return z.object({
    accountHolderEmailAddress: createAccountHolderEmailAddressSchema(t),
    mainTraveler: createBaseExperienceTravelerDetailsSchema(startDate)(mainTraveler, t).and(
      createMainTravelerAdditionalFieldsSchema(t)
    ),
    additionalTravelers: createAdditionalTravelersSchema(
      additionalTravelers,
      createBaseExperienceTravelerDetailsSchema(startDate),
      t
    ),
  })
}

export type ExperienceTravelerDetails = z.infer<
  ReturnType<typeof createExperienceTravelerDetailsSchema>
>

export type TravelerDetails = FlightHotelTravelerDetails | ExperienceTravelerDetails
export type MainTravelerDetails = TravelerDetails['mainTraveler']
export type AdditionalTravelerDetails = NonNullable<TravelerDetails['additionalTravelers']>[number]

export const createTravelerDetailsSchema = (
  context: FlightHotelTravelerContext | ExperienceTravelerContext,
  t?: TranslationFunc
) => {
  if (context.type === 'experience') {
    return createExperienceTravelerDetailsSchema(context, t)
  }
  return createFlightHotelTravelerDetailsSchema(context, t)
}

export const safeCreateTravelerDetailsSchema = (
  context: FlightHotelTravelerContext | ExperienceTravelerContext,
  t?: TranslationFunc
) => {
  try {
    return createTravelerDetailsSchema(context, t)
  } catch (error) {
    return undefined
  }
}
